/**
 * StaffProductivityReportPage — `/reports/staff-productivity`.
 *
 * Wired to GET /v1/reports/staff-productivity?period=&staffId= on
 * service-reporting-api. Uses audit log as a proxy for staff action counts.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';

import { getStaffProductivityReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

export function StaffProductivityReportPage(): JSX.Element {
  const report = findPreBuiltReport('staff-productivity');
  const [period, setPeriod] = useState<string>('THIS_MONTH');
  const [staffId, setStaffId] = useState<string>('');

  const query = useQuery({
    queryKey: ['pre-built', 'staff-productivity', period, staffId],
    queryFn: () => getStaffProductivityReport({ period, staffId: staffId || undefined }),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  return (
    <ReportShell report={report}>
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Period</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white">
              <option value="THIS_MONTH">This month</option>
              <option value="LAST_MONTH">Last month</option>
              <option value="THIS_YEAR">This year</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Staff ID</label>
            <input type="text" value={staffId} onChange={(e) => setStaffId(e.target.value)}
              placeholder="Leave blank for all" aria-label="Staff ID"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Total actions" value={data ? data.totalActions.toLocaleString('en-US') : '—'} hint="audit log proxy" icon={Users} />
        <KPICard title="Unique staff" value={data ? data.uniqueStaff.toLocaleString('en-US') : '—'} hint="distinct operators" />
        <KPICard title="Period" value={data?.period ?? '—'} hint="date range" />
        <KPICard title="Top actor actions" value={data && data.rows.length > 0 ? data.rows[0]?.actionCount.toLocaleString('en-US') ?? '—' : '—'} hint="highest count" />
      </div>

      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readStaffProductivity</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">{query.error.message}</DeferredNotice>
      )}

      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Staff action breakdown</h3>
            {data.stub && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Preview data — full data coming soon
              </span>
            )}
          </div>
          {data.rows.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No staff activity found for the period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Staff ID</th>
                    <th className="text-right px-4 py-2">Actions</th>
                    <th className="text-left px-4 py-2">Entity types</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.rows.map((row, i) => (
                    <tr key={row.staffId ?? `__sys_${i}`}>
                      <td className="px-4 py-2 font-mono text-xs">{row.staffId ?? '(system)'}</td>
                      <td className="px-4 py-2 text-right text-xs font-semibold">{row.actionCount.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-xs text-muted">{row.entityTypes.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ReportShell>
  );
}
